import React, { useMemo, useState, useEffect, useCallback, useContext } from 'react';

import { debugLog as log } from 'shared-services/logger-service';

import { ThemeContext } from 'context/ThemeContext';

import WebexLib from 'components/WebexLib';
import ZoomLib from 'components/ZoomLib';

export const MeetingEmbedContext = React.createContext();

export const MeetingEmbedProvider = ({ children }) => {
    const themeCtx = useContext(ThemeContext);
    const [inApp, setInApp] = useState(false);
    const [provider, setProvider] = useState(null);

    const [app, setApp] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [user, setUser] = useState(null);

    const handleWebexLibLoad = useCallback(() => {
        const app = window.Webex?.Application();
        setApp(app);

        const webexApp = window.webex.application;

        webexApp.onReady().then(async () => {
            log('[WEBEX] Ready', app);
            const webexTheme = app.theme;

            if (webexTheme) {
                themeCtx?.setThemeOverride(webexTheme.toLowerCase());
            }

            webexApp
                .listen()
                .then(() => {
                    webexApp.on('application:themeChanged', theme => {
                        themeCtx?.setThemeOverride(theme.toLowerCase());
                    });
                });

            try {
                const user = await app.context.getUser();
                setLoaded(true);
                setUser(user);
            } catch (e) {
                console.error('[NIFTY WEBEX] User get error', e);
            }
        });
    }, []);

    const handleZoomLibLoad = useCallback(async () => {
        // Enforce dark mode for Zoom
        themeCtx?.setThemeOverride('dark');

        const app = await window.zoomSdk.config({
            capabilities: [
                'getMeetingContext',
                'getUserContext',
                'getRunningContext',
                'getSupportedJsApis',
                'onSendAppInvitation',
                'onShareApp',
                'openUrl',
                'setVirtualBackground',
                'showNotification',
                'removeVirtualBackground',
            ]
        });

        setLoaded(true);

        const user = await window.zoomSdk.callZoomApi('getUserContext');

        setUser({
            id: user.participantId,
            displayName: user.screenName,
            role: user.name
        });
    }, []);

    const handleMessage = event => {
        if (
            event.data?.type === 'DATA' &&
            'user' in event.data?.data
        ) {
            setInApp(true);
            window.removeEventListener('message', handleMessage);
        }
    }

    // TODO: Make sure that user.token is available
    // TODO: Check QuickSignup
    const value = useMemo(() => ({
        inApp,
        app,
        user,
        loaded,
        provider,
        setProvider
    }), [inApp, app, user, loaded, provider, setProvider]);

    useEffect(() => {
        setInApp(Boolean(provider));
    }, [provider]);

    useEffect(() => {
        if (inApp) {
            if (provider === 'webex') {
                window.inWebex = true;
            } else {
                window.inZoom = true;
            }
            document.body.classList.add('in-meeting-embed', `in-meeting-${provider}`);
        } else {
            if (provider === 'webex') {
                window.inWebex = false;
            } else {
                window.inZoom = false;
            }
            document.body.classList.remove('in-meeting-embed', `in-meeting-${provider}`);
        }
    }, [inApp, provider]);

    useEffect(() => {
        window.addEventListener('message', handleMessage);

        window.parent.postMessage({
            action: 'SEND_DATA'
          }, '*');

        return () => {
            window.removeEventListener('message', handleMessage);
        }
    }, []);

    return (
        <MeetingEmbedContext.Provider value={value}>
            {
                provider === 'webex' ?
                    <WebexLib asyncScriptOnLoad={handleWebexLibLoad} />
                : provider === 'zoom' ?
                    <ZoomLib asyncScriptOnLoad={handleZoomLibLoad} />
                : null
            }

            { children }
        </MeetingEmbedContext.Provider>
    )
};
